import api from './index';

/**
 * EICR AI Observation Helper API client.
 * Calls /api/eicr/ai/observation-helper/start and .../continue.
 */

/**
 * @typedef {Object} EICRContext
 * @property {string} premisesType - 'Domestic' | 'Commercial' | 'Industrial' | 'Agricultural' | 'Educational' | 'Medical'
 * @property {string} earthingArrangement - 'TN-S' | 'TN-C-S' | 'TN-C' | 'TT' | 'IT' | ''
 * @property {string|null} dbLocation
 * @property {any} [testResults] - Optional test data snippets
 * @property {string|null} existingNotes
 */

/**
 * @typedef {Object} ObservationSeed
 * @property {string|null} location
 * @property {string|null} circuitRef
 */

/**
 * @typedef {Object} StartOptions
 * @property {boolean} [escalate] - Use GPT-5.2 (e.g. for "Retry with GPT-5.2")
 */

/**
 * Start the observation helper flow. Sends initial note and optionally observation seed.
 * Returns either questions (needs_more_info) or draft (ready_preview).
 *
 * @param {EICRContext} eicrContext
 * @param {string} initialNote
 * @param {ObservationSeed|null} observationSeed
 * @param {StartOptions} [options]
 * @returns {Promise<{ status: 'needs_more_info' | 'ready_preview', questions?: Array<{key: string, prompt: string, type: string, options?: string[], required: boolean}>, draft?: object, modelUsed?: string }>}
 */
export function startObservationHelper(eicrContext, initialNote, observationSeed, options = {}) {
  const body = {
    eicrContext,
    initialNote: initialNote?.trim() ?? '',
    observationSeed: observationSeed ?? null,
  };
  if (options.escalate) body.escalate = true;
  return api.post('/eicr/ai/observation-helper/start', body).then((r) => r.data);
}

/**
 * Continue the observation helper flow with answers. Used when status was needs_more_info.
 * Returns either more questions (needs_more_info) or draft (ready_preview).
 *
 * @param {EICRContext} eicrContext
 * @param {string} initialNote
 * @param {Record<string, any>} answers
 * @param {ObservationSeed|null} observationSeed
 * @param {{ escalate?: boolean }} [options]
 * @returns {Promise<{ status: 'needs_more_info' | 'ready_preview', questions?: Array<{key: string, prompt: string, type: string, options?: string[], required: boolean}>, draft?: object, modelUsed?: string }>}
 */
export function continueObservationHelper(eicrContext, initialNote, answers, observationSeed, options = {}) {
  const body = {
    eicrContext,
    initialNote: initialNote?.trim() ?? '',
    answers: answers ?? {},
    observationSeed: observationSeed ?? null,
  };
  if (options?.escalate) body.escalate = true;
  return api.post('/eicr/ai/observation-helper/continue', body).then((r) => r.data);
}

export const eicrAI = {
  startObservationHelper,
  continueObservationHelper,
};
